// Decoração sazonal do Campus Agzos: Carnaval, Páscoa, aniversário do dev (agosto), festa
// junina (junho), Halloween (15/out–2/nov), Natal (1/dez–6/jan) e o padrão do dia a dia. Duas partes:
//
//   - a ARTE (SEASONAL_FURNITURE): móveis de chão 1×1 e itens de parede, com as mesmas regras
//     do resto do catálogo (ver ../ARTE.md). Já estão em furniture/index.ts;
//   - a LÓGICA pura (seasonOf, seasonalDecor): escolhe a estação do dia e espalha a decoração
//     de forma determinística (stableHash), nas salas e no exterior. Nunca bloqueia o caminho
//     de uma sala, nunca fica em cima de móvel e nunca cai na rua.
//
// Só tokens da paleta. Luz do alto-esquerda, contorno tom 0. Paleta sem laranja: a abóbora
// usa a rampa 'pot' (terracota).

import { mapSize, type RoomMap } from '../../core/world.js';
import { stableHash } from '../../core/hash.js';
import { roomData } from '../engine/room-data.js';
import type { CampusLayout, PlacedMap } from '../world/layout.js';
import { FURNITURE_BY_ID } from './furniture/index.js';
import { Pix } from './pix.js';
import type { FurnitureDef } from './types.js';

/** Estação do dia. 'padrao' é o tema de todo dia sem data festiva. */
export type Season = 'carnaval' | 'pascoa' | 'aniversario' | 'junina' | 'halloween' | 'natal' | 'padrao';

/** Todos os temas, em ordem de prioridade (o último é o do dia a dia). */
export const SEASONS: readonly Season[] = ['carnaval', 'pascoa', 'aniversario', 'junina', 'halloween', 'natal', 'padrao'];

/** Um item colocado pela decoração: (x, y) em tiles do mundo, canto superior esquerdo da pegada. */
export interface SeasonalPlacement {
  item: string;
  x: number;
  y: number;
  /** Item de parede: y é a linha 0 da sala. */
  wall?: boolean;
  /** Id da sala no mundo (PlacedMap.id). Ausente no exterior. */
  roomId?: string;
}

/** Itens de cada estação: chão (1×1), parede e, se for outro, o exterior (gramado). */
const SEASON_ITEMS: Record<Season, { floor: readonly string[]; wall: readonly string[]; outdoor?: readonly string[] }> = {
  carnaval: { floor: ['confete', 'tambor'], wall: ['mascara-carnaval', 'serpentina'] },
  pascoa: { floor: ['ovo-pascoa', 'coelho-pascoa', 'cesta-ovos'], wall: ['guirlanda-pascoa'], outdoor: ['ovo-pascoa', 'coelho-pascoa'] },
  aniversario: { floor: ['baloes', 'bolo-aniversario', 'presente-aniversario', 'chapeu-festa'], wall: ['faixa-parabens'], outdoor: ['baloes', 'presente-aniversario'] },
  junina: { floor: ['fogueira'], wall: ['bandeirinhas'] },
  halloween: { floor: ['abobora', 'abobora-grande', 'fantasma'], wall: ['morcego', 'teia'] },
  natal: { floor: ['arvore-natal', 'presente', 'boneco-neve'], wall: ['guirlanda', 'pisca-pisca'] },
  padrao: { floor: ['vaso-flores', 'luminaria-mesa'], wall: ['quadro-agzos', 'planta-suspensa'], outdoor: ['vaso-flores'] },
};

/**
 * Densidade: itens de chão por sala (sorteados entre floor[0] e floor[1]), itens de parede
 * (1 a wallMax) e o exterior (1 tile em N). A festa enfeita mais; o padrão é discreto.
 */
interface Density {
  floor: readonly [number, number];
  wallMax: number;
  outdoorEvery: number;
}
const FESTA: Density = { floor: [1, 3], wallMax: 2, outdoorEvery: 45 };
const PADRAO: Density = { floor: [0, 1], wallMax: 1, outdoorEvery: 120 };
const DIRS4: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];

// ---------------------------------------------------------------- arte

/** Elipse cheia (Pix só tem círculo). */
function ellipse(pix: Pix, cx: number, cy: number, rx: number, ry: number, c: string): Pix {
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      if (((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1) pix.px(x, y, c);
    }
  }
  return pix;
}

/** Bola com sombra à direita-baixo e luz no alto-esquerda (boneco de neve). */
function ball(pix: Pix, cx: number, cy: number, rx: number, ry: number): Pix {
  ellipse(pix, cx + 1, cy + 1, rx, ry, 'd');
  ellipse(pix, cx, cy, rx, ry, 'b');
  return ellipse(pix, cx - rx / 3, cy - ry / 3, rx / 2.5, ry / 2.5, 'l');
}

const PUMPKIN = { o: 'pot0', d: 'pot1', b: 'pot2', l: 'pot3', T: 'trunk2', u: 'trunk1', Y: 'light2', g: 'leaf2', G: 'leaf3' };

/** Abóbora de rosto talhado (16×14) ou abóbora grande sem rosto (16×16), com talo e uma folha. */
function pumpkin(big: boolean): Pix {
  const pix = new Pix(16, big ? 16 : 14);
  const cy = big ? 9 : 7.5;
  const ry = big ? 5 : 4.5;
  ellipse(pix, 8.5, cy + 0.5, 7, ry, 'd');
  ellipse(pix, 7.5, cy, 6.5, ry, 'b');
  ellipse(pix, 5.5, cy - 1.5, 3, 2, 'l');
  // gomos
  const ribs = big ? [4, 7, 11] : [5, 10];
  for (const x of ribs) pix.vline(x, cy - 2, 5, 'd');
  // talo e folha
  pix.rect(7, 0, 2, big ? 4 : 3, 'T').vline(8, 0, big ? 4 : 3, 'u');
  pix.px(10, 1, 'g').px(11, 1, 'G').px(12, 0, 'g');
  if (!big) {
    // rosto talhado: olhos quadrados e boca com dentes
    pix.rect(3, 6, 2, 2, 'Y').rect(11, 6, 2, 2, 'Y');
    pix.hline(4, 9, 8, 'Y').px(4, 10, 'Y').px(6, 10, 'Y').px(9, 10, 'Y').px(11, 10, 'Y');
  }
  return pix.outline('o');
}

// cinza-chumbo em vez de preto: o morcego precisa aparecer no fundo escuro e na parede clara
const BAT = { o: 'fabricGray0', k: 'fabricGray1', K: 'fabricGray2', e: 'red3' };

/** Morcego (16×10) de asas abertas: frame 0 para cima, frame 1 para baixo. */
function bat(frame: 0 | 1): Pix {
  const pix = new Pix(16, 10);
  for (let x = 0; x <= 5; x++) {
    const t = x / 5; // 0 na ponta da asa, 1 no corpo
    const lerp = (a: number, b: number) => Math.round(a + (b - a) * t);
    const top = frame === 0 ? lerp(0, 2) : lerp(4, 3);
    const bottom = frame === 0 ? lerp(4, 7) : lerp(9, 7);
    for (let y = top; y <= bottom; y++) {
      // a borda de baixo das asas tem um dente: o recorte no meio de cada asa
      if (y === bottom && x === 2) continue;
      pix.px(x, y, 'k').px(15 - x, y, 'k');
    }
    pix.px(x, top, 'K').px(15 - x, top, 'K');
  }
  // corpo, cabeça e orelhas
  ellipse(pix, 7.5, 5.5, 2.5, 3, 'k');
  ellipse(pix, 7.5, 2.5, 2, 1.8, 'k');
  pix.px(6, 0, 'k').px(9, 0, 'k');
  pix.px(6, 2, 'e').px(9, 2, 'e');
  return pix.outline('o');
}

const WEB = { w: 'concrete2' };

/** Teia de canto (16×12): raios saindo do canto de cima à esquerda e três arcos. */
function web(): Pix {
  const pix = new Pix(16, 12);
  const quarter = Math.PI / 2;
  for (let a = 0; a <= 4; a++) {
    const ang = (a / 4) * quarter;
    for (let t = 0; t <= 1; t += 0.02) pix.px(Math.round(t * 15 * Math.cos(ang)), Math.round(t * 11 * Math.sin(ang)), 'w');
  }
  for (const level of [0.35, 0.65, 0.95]) {
    for (let ang = 0; ang <= quarter; ang += 0.02) pix.px(Math.round(level * 15 * Math.cos(ang)), Math.round(level * 11 * Math.sin(ang)), 'w');
  }
  return pix;
}

const GHOST = { o: 'white0', d: 'white1', b: 'white2', l: 'white3', k: 'ink' };

/** Fantasma (16×18): corpo com barra de baixo ondulada. Frame 1 inverte as ondas (flutua). */
function ghost(frame: 0 | 1): Pix {
  const pix = new Pix(16, 18);
  ellipse(pix, 7.5, 7, 6, 6, 'b');
  pix.rect(2, 7, 12, 6, 'b');
  for (let x = 2; x < 14; x++) {
    const end = (x + frame) % 4 < 2 ? 16 : 14;
    pix.rect(x, 13, 1, end - 13, 'b');
  }
  pix.rect(11, 4, 2, 9, 'd');
  ellipse(pix, 5, 4, 2, 2, 'l');
  pix.rect(5, 7, 2, 3, 'k').rect(10, 7, 2, 3, 'k');
  pix.rect(7, 11, 2, 1, 'k');
  return pix.outline('o');
}

const TREE = { o: 'leaf0', d: 'leaf1', g: 'leaf2', G: 'leaf3', T: 'trunk2', u: 'trunk1', s: 'gold3', S: 'light3', r: 'red3', b: 'fabricBlue3', y: 'light2' };

/** Árvore de Natal (16×32): três camadas de cone, estrela, bolas que trocam de cor entre os quadros. */
function xmasTree(frame: 0 | 1): Pix {
  const pix = new Pix(16, 32);
  const tiers: Array<[number, number, number]> = [
    [6, 15, 4],
    [11, 22, 6],
    [17, 27, 7],
  ];
  for (const [top, bottom, half] of tiers) {
    for (let y = top; y <= bottom; y++) {
      const r = Math.round(half * (0.35 + (0.65 * (y - top)) / (bottom - top)));
      for (let x = 8 - r; x < 8 + r; x++) {
        const c = y === bottom ? 'd' : x < 8 - r / 3 ? 'G' : x >= 8 + r / 2 ? 'd' : 'g';
        pix.px(x, y, c);
      }
    }
  }
  pix.rect(7, 28, 2, 3, 'T').vline(8, 28, 3, 'u');
  pix.disc(8, 2, 2, 's').px(8, 2, 'S');
  const bulbs: Array<[number, number]> = [[6, 10], [10, 12], [5, 17], [11, 19], [8, 14], [4, 22], [12, 23], [7, 21], [9, 26], [6, 25], [10, 16]];
  const colors = ['r', 'b', 'y'];
  bulbs.forEach(([x, y], i) => pix.px(x, y, colors[(i + frame) % colors.length]!));
  return pix.outline('o');
}

const GIFT = { o: 'red0', r: 'red2', R: 'red3', d: 'red1', y: 'gold2', Y: 'gold3' };

/** Presente (16×16): caixa vermelha com fita dourada em cruz e laço no topo. */
function gift(): Pix {
  const pix = new Pix(16, 16);
  pix.box(1, 6, 14, 9, { fill: 'r', light: 'R', dark: 'd' });
  pix.box(0, 3, 16, 4, { fill: 'r', light: 'R', dark: 'd' });
  pix.rect(7, 3, 2, 12, 'y').rect(0, 4, 16, 1, 'y');
  pix.disc(5, 2, 2, 'Y').disc(10, 2, 2, 'Y');
  pix.px(7, 2, 'y').px(8, 2, 'y');
  return pix.outline('o');
}

const SNOW = { o: 'white0', d: 'white1', b: 'white2', l: 'white3', k: 'black1', K: 'ink', p: 'pot2', r: 'red2', R: 'red3' };

/** Boneco de neve (16×24): três bolas, cenoura, chapéu preto e cachecol vermelho. */
function snowman(): Pix {
  const pix = new Pix(16, 24);
  ball(pix, 7.5, 17, 6, 5);
  ball(pix, 7.5, 10.5, 4.5, 4);
  ball(pix, 7.5, 5, 3.5, 3);
  pix.rect(4, 0, 7, 2, 'k').hline(3, 2, 9, 'k');
  pix.rect(4, 8, 8, 2, 'r').hline(10, 9, 2, 'R');
  pix.rect(6, 4, 1, 1, 'K').rect(9, 4, 1, 1, 'K');
  pix.px(8, 5, 'p').px(9, 5, 'p');
  pix.px(8, 11, 'k').px(8, 14, 'k');
  return pix.outline('o');
}

const WREATH = { g: 'leaf2', G: 'leaf3', d: 'leaf1', r: 'red2', R: 'red3' };

/** Guirlanda de parede (16×14): anel de folhas com laço vermelho embaixo. */
function wreath(): Pix {
  const pix = new Pix(16, 14);
  for (let a = 0; a < 360; a += 5) {
    const rad = (a * Math.PI) / 180;
    for (const k of [0.9, 1]) {
      const x = Math.round(7.5 + 6.5 * k * Math.cos(rad));
      const y = Math.round(6 + 5.5 * k * Math.sin(rad));
      pix.px(x, y, a % 30 < 15 ? 'g' : 'G');
    }
  }
  pix.px(7, 0, 'd').px(8, 0, 'd');
  pix.rect(6, 11, 4, 2, 'r').px(7, 12, 'R');
  pix.px(4, 9, 'R').px(11, 9, 'R');
  return pix;
}

const LIGHTS = { m: 'metal1', r: 'red3', y: 'light3', g: 'fabricGreen3', b: 'fabricBlue3' };

/** Pisca-pisca de parede (32×8): fio com leve curva e lâmpadas que trocam de cor entre os quadros. */
function lights(frame: 0 | 1): Pix {
  const pix = new Pix(32, 8);
  const wire = (x: number) => 1 + Math.round(2 * Math.sin((Math.PI * x) / 31));
  for (let x = 0; x < 32; x++) pix.px(x, wire(x), 'm');
  const colors = ['r', 'y', 'g', 'b'];
  for (let i = 0; i < 7; i++) {
    const x = 2 + i * 4;
    const y = wire(x);
    pix.px(x, y, 'm').rect(x, y + 1, 2, 3, colors[(i + frame) % colors.length]!);
  }
  return pix;
}

const FLAGS = { m: 'metal2', r: 'red2', R: 'red3', y: 'fabricMustard2', Y: 'fabricMustard3', b: 'fabricBlue2', B: 'fabricBlue3', g: 'fabricGreen2', G: 'fabricGreen3' };

/** Bandeirinhas de festa junina (32×12): barbante com leve curva e cinco bandeirinhas triangulares. */
function flags(): Pix {
  const pix = new Pix(32, 12);
  const wire = (x: number) => 1 + Math.round(1.5 * Math.sin((Math.PI * x) / 31));
  for (let x = 0; x < 32; x++) pix.px(x, wire(x), 'm');
  ['r', 'y', 'b', 'g', 'r'].forEach((c, i) => {
    const cx = 3 + i * 6;
    const top = wire(cx) + 1;
    const light = c.toUpperCase();
    for (let r = 0; r < 5; r++) {
      const half = 2 - Math.floor(r / 2);
      for (let x = cx - half; x <= cx + half; x++) pix.px(x, top + r, x === cx - half ? light : c);
    }
  });
  return pix;
}

const FIRE = { o: 'black0', r: 'red2', R: 'red3', y: 'light2', Y: 'light3', t: 'trunk2', u: 'trunk1', c: 'concrete2', C: 'concrete1' };

/** Fogueira (16×16): toras cruzadas, pedras e chama em três camadas. Frame 1 muda a ponta da chama. */
function bonfire(frame: 0 | 1): Pix {
  const pix = new Pix(16, 16);
  ellipse(pix, 8, 14.5, 7, 1.8, 'C');
  for (const x of [2, 5, 9, 12]) pix.px(x, 15, 'c');
  // toras: uma deitada e outra cruzada por cima, com a casca escura embaixo
  pix.rect(2, 12, 12, 2, 't').hline(2, 13, 12, 'u');
  pix.rect(4, 10, 8, 2, 't').hline(4, 11, 8, 'u');
  // chama em três camadas, mais larga na base; o quadro 1 muda a ponta
  const lean = frame ? 1 : 0;
  ellipse(pix, 8 - lean * 0.5, 7, 5, 5.5, 'r');
  ellipse(pix, 8, 8.5, 3.5, 4.5, 'y');
  ellipse(pix, 8, 10, 1.8, 2.5, 'Y');
  pix.px(8 + lean, 0, 'R').px(8 + lean, 1, 'R');
  return pix.outline('o');
}

// ---------------------------------------------------------------- Páscoa

const EGG = { o: 'paper0', d: 'paper1', b: 'paper2', l: 'paper3', r: 'red1', R: 'red2', Q: 'red3', u: 'fabricBlue2', m: 'fabricMustard3' };

/** Ovo decorado (16×16): casca creme com faixa vermelha, zigue-zague azul e pontos amarelos. */
function egg(): Pix {
  const pix = new Pix(16, 16);
  ellipse(pix, 8.5, 8.5, 5, 6.5, 'd');
  ellipse(pix, 7.5, 7.5, 5, 6.5, 'b');
  ellipse(pix, 5.5, 5, 1.5, 2.5, 'l');
  // as faixas trocam o tom da casca e mantêm a luz e a sombra
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const c = pix.get(x, y);
      if (c !== 'b' && c !== 'l' && c !== 'd') continue;
      if (y >= 4 && y <= 6) pix.px(x, y, c === 'b' ? 'R' : c === 'l' ? 'Q' : 'r');
      else if (y >= 9 && y <= 10 && (x + y) % 4 < 2) pix.px(x, y, 'u');
      else if (y === 12 && x % 3 === 1) pix.px(x, y, 'm');
    }
  }
  return pix.outline('o');
}

const BUNNY = { o: 'white0', d: 'white1', b: 'white2', l: 'white3', k: 'ink', p: 'blush' };

/** Coelho sentado (16×16) de frente: orelhas compridas com a parte de dentro rosa, rabinho branco. */
function bunny(): Pix {
  const pix = new Pix(16, 16);
  // corpo
  ellipse(pix, 8.5, 12, 4.5, 3.5, 'd');
  ellipse(pix, 8, 11.5, 4.5, 3.5, 'b');
  ellipse(pix, 6.5, 10.5, 2, 1.5, 'l');
  // cabeça
  ellipse(pix, 8.5, 7, 3.5, 3, 'd');
  ellipse(pix, 8, 6.5, 3.5, 3, 'b');
  ellipse(pix, 6.5, 5.5, 1.5, 1, 'l');
  // orelhas
  ellipse(pix, 5.5, 2.5, 1.3, 2.7, 'b');
  ellipse(pix, 10.5, 2.5, 1.3, 2.7, 'b');
  pix.px(5, 2, 'p').px(5, 3, 'p').px(10, 2, 'p').px(10, 3, 'p');
  // olhos, focinho e rabinho
  pix.px(6, 6, 'k').px(10, 6, 'k').px(8, 8, 'p');
  ellipse(pix, 12.5, 13, 1.3, 1.3, 'l');
  return pix.outline('o');
}

const BASKET = { o: 'oak0', d: 'oak1', b: 'oak2', l: 'oak3', r: 'red1', R: 'red2', Q: 'red3', u: 'fabricBlue1', U: 'fabricBlue2', V: 'fabricBlue3', m: 'fabricMustard1', M: 'fabricMustard2', N: 'fabricMustard3' };

/** Cesta de vime (16×16) com três ovos saindo. A borda da cesta esconde a base dos ovos. */
function basket(): Pix {
  const pix = new Pix(16, 16);
  const eggs: Array<[number, number, [string, string, string]]> = [
    [4.5, 6.5, ['r', 'R', 'Q']],
    [8.5, 5, ['u', 'U', 'V']],
    [11.5, 6.5, ['m', 'M', 'N']],
  ];
  for (const [cx, cy, [dark, base, light]] of eggs) {
    ellipse(pix, cx, cy, 2.2, 3, dark);
    ellipse(pix, cx - 0.4, cy - 0.4, 1.8, 2.6, base);
    ellipse(pix, cx - 1, cy - 1.2, 0.7, 1, light);
  }
  // trama de tiras de vime, borda de cima clara e base escura
  for (let y = 9; y <= 15; y++) {
    for (let x = 2; x <= 13; x++) {
      if (y === 15 && (x === 2 || x === 13)) continue;
      pix.px(x, y, (x + y) % 3 === 0 ? 'd' : 'b');
    }
  }
  pix.hline(2, 9, 12, 'l').hline(3, 15, 10, 'd');
  return pix.outline('o');
}

const EASTER_WREATH = { o: 'leaf0', d: 'leaf1', g: 'leaf2', G: 'leaf3', r: 'red2', R: 'red3', b: 'fabricBlue2', m: 'fabricMustard2', M: 'fabricMustard3' };

/** Guirlanda de Páscoa (16×14): anel de folhas com ovinhos pendurados e laço amarelo embaixo. */
function easterWreath(): Pix {
  const pix = new Pix(16, 14);
  for (let a = 0; a < 360; a += 5) {
    const rad = (a * Math.PI) / 180;
    for (const k of [0.9, 1]) {
      const x = Math.round(7.5 + 6.5 * k * Math.cos(rad));
      const y = Math.round(6 + 5.5 * k * Math.sin(rad));
      pix.px(x, y, a % 30 < 15 ? 'g' : 'G');
    }
  }
  pix.px(7, 0, 'd').px(8, 0, 'd');
  pix.rect(2, 2, 2, 3, 'r').rect(12, 3, 2, 3, 'b').rect(3, 9, 2, 3, 'm');
  pix.rect(6, 11, 4, 2, 'M').px(7, 12, 'm');
  return pix;
}

// ---------------------------------------------------------------- Carnaval

const MASK = { o: 'black0', g: 'gold2', G: 'gold3', f: 'fabricBlue2', F: 'fabricBlue3', k: 'ink', r: 'red2', R: 'red3', m: 'fabricMustard3', n: 'fabricGreen2', N: 'fabricGreen3' };

/** Máscara de baile (16×12): duas aberturas com aro dourado, ponte e penas no topo. */
function carnivalMask(): Pix {
  const pix = new Pix(16, 12);
  for (const cx of [4.5, 11.5]) {
    ellipse(pix, cx, 6, 4, 3, 'g');
    ellipse(pix, cx, 6, 3.3, 2.5, 'f');
    ellipse(pix, cx - 0.8, 4.8, 1.3, 0.8, 'F');
    ellipse(pix, cx + 0.2, 6.3, 1.8, 1.2, 'k');
  }
  pix.rect(7, 4, 2, 2, 'G');
  const feathers: Array<[number, number, string]> = [
    [3, 1, 'r'],
    [2, 2, 'R'],
    [5, 0, 'm'],
    [6, 1, 'm'],
    [9, 1, 'n'],
    [10, 0, 'N'],
    [12, 1, 'r'],
    [13, 2, 'R'],
  ];
  for (const [x, y, c] of feathers) pix.px(x, y, c);
  return pix.outline('o');
}

const STREAMERS = { r: 'red2', m: 'fabricMustard2', b: 'fabricBlue2', g: 'fabricGreen2' };

/** Serpentina (32×12, 2 tiles): fita em arco com tiras caindo. O quadro 1 troca as cores e o comprimento. */
function streamers(frame: 0 | 1): Pix {
  const pix = new Pix(32, 12);
  const colors = ['r', 'm', 'b', 'g'];
  const arc = (x: number) => 1 + Math.round(3 * Math.sin((Math.PI * x) / 31));
  for (let x = 0; x < 32; x++) pix.vline(x, arc(x), 2, colors[(Math.floor(x / 4) + frame) % colors.length]!);
  for (let i = 0; i < 4; i++) {
    const x = 2 + i * 8;
    pix.vline(x, arc(x) + 2, 3 + ((i + frame) % 2) * 2, colors[(i + 2 + frame) % colors.length]!);
  }
  return pix;
}

const CONFETTI = { r: 'red2', R: 'red3', b: 'fabricBlue3', m: 'fabricMustard3', g: 'fabricGreen3', w: 'white3' };

/** Confete (16×16) espalhado no chão: pedacinhos de papel, sem contorno. Não bloqueia a caminhada. */
function confetti(): Pix {
  const pix = new Pix(16, 16);
  const bits: Array<[number, number, string, number]> = [
    [2, 3, 'r', 2],
    [7, 2, 'b', 1],
    [12, 4, 'm', 2],
    [4, 8, 'g', 1],
    [9, 7, 'R', 2],
    [13, 10, 'b', 1],
    [2, 12, 'm', 2],
    [7, 13, 'g', 2],
    [11, 14, 'r', 1],
    [5, 5, 'w', 1],
    [14, 14, 'w', 1],
  ];
  for (const [x, y, c, len] of bits) pix.hline(x, y, len, c);
  return pix;
}

const DRUM = { o: 'black0', r: 'red1', R: 'red2', Q: 'red3', p: 'paper2', P: 'paper3', m: 'fabricMustard3', W: 'oak3' };

/** Tambor (16×13) de frente, com a pele clara em cima, cordas amarelas e duas baquetas cruzadas. */
function drum(): Pix {
  const pix = new Pix(16, 13);
  pix.box(2, 4, 12, 7, { fill: 'R', light: 'Q', dark: 'r' });
  ellipse(pix, 8, 4.5, 6, 2.5, 'p');
  ellipse(pix, 7, 4, 4, 1.2, 'P');
  for (const x of [5, 8, 11]) pix.vline(x, 7, 3, 'm');
  for (let i = 0; i <= 6; i++) {
    pix.px(1 + i, Math.round(i * 0.5), 'W').px(14 - i, Math.round(i * 0.5), 'W');
  }
  return pix.outline('o');
}

// ---------------------------------------------------------------- aniversário

const BALLOON = { o: 'black0', k: 'metal2', s: 'metal1', r: 'red1', R: 'red2', Q: 'red3', b: 'fabricBlue1', B: 'fabricBlue2', C: 'fabricBlue3', y: 'fabricMustard1', Y: 'fabricMustard2', Z: 'fabricMustard3' };

/** Um balão (rx 2,8 e ry 3,8) com sombra embaixo à direita. Devolve o ponto do nó, de onde sai o fio. */
function balloon(pix: Pix, cx: number, cy: number, [dark, base, light]: readonly [string, string, string]): [number, number] {
  ellipse(pix, cx + 0.6, cy + 0.6, 2.8, 3.8, dark);
  ellipse(pix, cx, cy, 2.8, 3.8, base);
  ellipse(pix, cx - 1, cy - 1.2, 0.9, 1.3, light);
  const knot: [number, number] = [Math.round(cx), Math.floor(cy + 3.8) + 1];
  pix.px(knot[0], knot[1], dark);
  return knot;
}

/** Três balões coloridos (16×32) com os fios convergindo para um peso. Os quadros balançam o balão vermelho e o azul. */
function balloons(frame: 0 | 1): Pix {
  const pix = new Pix(16, 32);
  const knots = [
    balloon(pix, 4 + frame, 10, ['r', 'R', 'Q']),
    balloon(pix, 11 - frame, 8, ['b', 'B', 'C']),
    balloon(pix, 8, 19, ['y', 'Y', 'Z']),
  ];
  pix.outline('o');
  // fios por cima do contorno (senão ganham borda preta); o peso fecha o conjunto embaixo
  for (const [x, y] of knots) pix.vline(x, y + 1, 29 - y, 'k');
  return pix.rect(6, 30, 4, 2, 's');
}

const CAKE = { o: 'black0', p: 'paper2', P: 'paper3', e: 'paper1', r: 'red2', Q: 'red3', q: 'red1', w: 'white1', f: 'fabricBlue2', m: 'fabricMustard2', g: 'fabricGreen2', l: 'light2', L: 'light3' };

/** Bolo (16×16): massa creme com cobertura de morango escorrendo e três velas. O quadro 1 aumenta a chama. */
function cake(frame: 0 | 1): Pix {
  const pix = new Pix(16, 16);
  ellipse(pix, 8, 13.5, 7, 2, 'w');
  pix.box(2, 9, 12, 5, { fill: 'p', light: 'P', dark: 'e' });
  pix.box(2, 7, 12, 2, { fill: 'r', light: 'Q', dark: 'q' });
  for (const x of [3, 7, 11]) pix.px(x, 9, 'r').px(x, 10, 'r');
  for (const [x, c] of [[5, 'f'], [8, 'm'], [11, 'g']] as const) {
    pix.vline(x, 4, 3, c);
    pix.px(x, 2, 'L').px(x, 3, 'l');
    if (frame) pix.px(x, 1, 'l');
  }
  return pix.outline('o');
}

// Fonte de pixel 3×5 para a faixa: só as letras de PARABÉNS. Cada letra ocupa 4 colunas (3 + 1 de espaço).
const FONT: Record<string, readonly string[]> = {
  P: ['###', '#.#', '###', '#..', '#..'],
  A: ['.#.', '#.#', '###', '#.#', '#.#'],
  R: ['##.', '#.#', '##.', '#.#', '#.#'],
  B: ['##.', '#.#', '##.', '#.#', '##.'],
  É: ['###', '#..', '##.', '#..', '###'],
  N: ['#.#', '##.', '#.#', '#.#', '#.#'],
  S: ['###', '#..', '###', '..#', '###'],
};

const BANNER = { d: 'red0', r: 'red1', R: 'red2', w: 'white3' };

/** Faixa de parede (32×14, 2 tiles) com PARABÉNS em branco sobre vermelho: letras 3×5, legíveis em pixel. */
function banner(): Pix {
  const pix = new Pix(32, 14);
  pix.box(0, 1, 32, 12, { fill: 'r', light: 'R', dark: 'd' });
  [...'PARABÉNS'].forEach((ch, i) => {
    const x = i * 4;
    FONT[ch]!.forEach((row, j) => [...row].forEach((c, k) => {
      if (c === '#') pix.px(x + k, 5 + j, 'w');
    }));
    // acento agudo do É, por cima da letra
    if (ch === 'É') pix.px(x + 1, 4, 'w').px(x + 2, 3, 'w');
  });
  return pix;
}

const PARTY_GIFT = { o: 'fabricBlue0', f: 'fabricBlue2', F: 'fabricBlue3', e: 'fabricBlue1', y: 'fabricMustard2', Y: 'fabricMustard3' };

/** Presente de aniversário (16×16): caixa azul com fita amarela em cruz e laço. */
function partyGift(): Pix {
  const pix = new Pix(16, 16);
  pix.box(1, 6, 14, 9, { fill: 'f', light: 'F', dark: 'e' });
  pix.box(0, 3, 16, 4, { fill: 'f', light: 'F', dark: 'e' });
  pix.rect(7, 3, 2, 12, 'y').rect(0, 4, 16, 1, 'y');
  pix.disc(5, 2, 2, 'Y').disc(10, 2, 2, 'Y');
  pix.px(7, 2, 'y').px(8, 2, 'y');
  return pix.outline('o');
}

const HAT = { o: 'black0', r: 'red2', R: 'red3', b: 'fabricBlue2', B: 'fabricBlue3', y: 'fabricMustard3', m: 'metal2' };

/** Chapéu de festa (16×16): cone listrado de vermelho e azul, pompom e elástico. */
function partyHat(): Pix {
  const pix = new Pix(16, 16);
  for (let y = 2; y <= 13; y++) {
    const half = ((y - 2) / 11) * 4.5;
    const left = Math.round(8 - half);
    const right = Math.round(8 + half);
    const stripe = Math.floor((y - 2) / 3) % 2 === 0 ? 'r' : 'b';
    // a borda da esquerda leva a luz (tom 3)
    for (let x = left; x < right; x++) pix.px(x, y, x === left ? stripe.toUpperCase() : stripe);
  }
  pix.disc(8, 2, 1.5, 'y');
  pix.hline(4, 13, 9, 'm');
  return pix.outline('o');
}

// ---------------------------------------------------------------- padrão

const VASE = { o: 'pot0', d: 'pot1', b: 'pot2', l: 'pot3', s: 'leaf2', S: 'leaf3', r: 'red2', m: 'fabricMustard3', w: 'paper3' };

/** Vaso de flores (16×16): vaso de barro com três flores e folhas. */
function flowerVase(): Pix {
  const pix = new Pix(16, 16);
  pix.vline(8, 5, 6, 's').vline(7, 7, 4, 'S');
  pix.px(6, 8, 's').px(5, 7, 's').px(10, 9, 's').px(11, 8, 'S');
  pix.disc(5, 4, 1.5, 'r').disc(11, 3, 1.5, 'm').disc(8, 2, 1.5, 'w');
  pix.box(4, 10, 8, 6, { fill: 'b', light: 'l', dark: 'd' });
  pix.box(3, 8, 10, 2, { fill: 'b', light: 'l', dark: 'd' });
  return pix.outline('o');
}

const FRAME = { o: 'oak0', d: 'oak1', b: 'oak2', l: 'oak3', w: 'paper3', W: 'paper2', r: 'red2' };

/** Quadro da marca (14×13, parede): moldura de madeira, tela clara e um A vermelho. */
function agzosFrame(): Pix {
  const pix = new Pix(14, 13);
  pix.box(0, 0, 14, 13, { fill: 'b', light: 'l', dark: 'd' });
  pix.rect(2, 2, 10, 9, 'w').hline(2, 10, 10, 'W');
  // o A da marca: ponta, travessa e pernas
  pix.hline(6, 4, 2, 'r').px(5, 5, 'r').px(8, 5, 'r');
  pix.hline(5, 6, 4, 'r');
  pix.px(5, 7, 'r').px(8, 7, 'r').px(5, 8, 'r').px(8, 8, 'r');
  return pix.outline('o');
}

const LAMP = { o: 'paper0', d: 'paper1', b: 'paper2', l: 'paper3', m: 'metal1', M: 'metal2', n: 'metal3' };

/** Luminária de mesa (16×16): abajur de tecido creme com base de metal. O glow é suave. */
function tableLamp(): Pix {
  const pix = new Pix(16, 16);
  ellipse(pix, 7.5, 14, 4, 1.5, 'm');
  pix.vline(7, 10, 4, 'M').vline(8, 10, 4, 'n');
  for (let y = 2; y <= 9; y++) {
    const half = 2.5 + ((y - 2) / 7) * 2.5;
    for (let x = Math.round(7.5 - half); x <= Math.round(7.5 + half); x++) {
      pix.px(x, y, y === 9 ? 'd' : x <= 7 ? 'l' : 'b');
    }
  }
  return pix.outline('o');
}

const HANGING = { o: 'black0', m: 'metal2', p: 'pot2', P: 'pot3', d: 'pot1', s: 'leaf2', S: 'leaf3' };

/** Planta suspensa (16×18, parede): vaso preso por duas cordas, com trepadeiras caindo em zigue-zague. */
function hangingPlant(): Pix {
  const pix = new Pix(16, 18);
  pix.vline(4, 0, 5, 'm').vline(11, 0, 5, 'm');
  pix.box(3, 5, 10, 4, { fill: 'p', light: 'P', dark: 'd' });
  for (const [x, len] of [[4, 8], [7, 6], [10, 9], [12, 5]] as const) {
    for (let i = 0; i < len; i++) pix.px(x + (i % 3 === 2 ? 1 : 0), 9 + i, i % 2 ? 'S' : 's');
  }
  return pix.outline('o');
}

/** Catálogo sazonal. Glow só aparece à noite (como janela e poste). */
export const SEASONAL_FURNITURE: readonly FurnitureDef[] = [
  {
    id: 'abobora',
    name: 'Abóbora',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: pumpkin(false).sprite(PUMPKIN),
    glow: { color: 'rgba(255, 150, 60, 0.35)', radius: 20 },
  },
  {
    id: 'abobora-grande',
    name: 'Abóbora grande',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: pumpkin(true).sprite(PUMPKIN),
  },
  {
    id: 'morcego',
    name: 'Morcego',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    wall: true,
    sprite: bat(0).sprite(BAT),
    frames: [bat(1).sprite(BAT)],
    frameMs: 350,
  },
  {
    id: 'teia',
    name: 'Teia de aranha',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    wall: true,
    sprite: web().sprite(WEB),
  },
  {
    id: 'fantasma',
    name: 'Fantasma',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    blocks: false,
    sprite: ghost(0).sprite(GHOST),
    frames: [ghost(1).sprite(GHOST)],
    frameMs: 600,
  },
  {
    id: 'arvore-natal',
    name: 'Árvore de Natal',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: xmasTree(0).sprite(TREE),
    frames: [xmasTree(1).sprite(TREE)],
    frameMs: 700,
    glow: { color: 'rgba(255, 215, 120, 0.3)', radius: 30, dy: -6 },
  },
  {
    id: 'presente',
    name: 'Presente',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: gift().sprite(GIFT),
  },
  {
    id: 'guirlanda',
    name: 'Guirlanda',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    wall: true,
    sprite: wreath().sprite(WREATH),
  },
  {
    id: 'pisca-pisca',
    name: 'Pisca-pisca',
    category: 'decor',
    footprint: { w: 2, h: 1 },
    wall: true,
    sprite: lights(0).sprite(LIGHTS),
    frames: [lights(1).sprite(LIGHTS)],
    frameMs: 500,
    glow: { color: 'rgba(255, 220, 140, 0.3)', radius: 24 },
  },
  {
    id: 'boneco-neve',
    name: 'Boneco de neve',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: snowman().sprite(SNOW),
  },
  {
    id: 'bandeirinhas',
    name: 'Bandeirinhas',
    category: 'decor',
    footprint: { w: 2, h: 1 },
    wall: true,
    sprite: flags().sprite(FLAGS),
  },
  {
    id: 'fogueira',
    name: 'Fogueira',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: bonfire(0).sprite(FIRE),
    frames: [bonfire(1).sprite(FIRE)],
    frameMs: 180,
    glow: { color: 'rgba(255, 140, 50, 0.4)', radius: 28, dy: -4 },
  },
  // Páscoa
  { id: 'ovo-pascoa', name: 'Ovo de Páscoa', category: 'decor', footprint: { w: 1, h: 1 }, sprite: egg().sprite(EGG) },
  { id: 'coelho-pascoa', name: 'Coelho de Páscoa', category: 'decor', footprint: { w: 1, h: 1 }, sprite: bunny().sprite(BUNNY) },
  { id: 'cesta-ovos', name: 'Cesta de ovos', category: 'decor', footprint: { w: 1, h: 1 }, sprite: basket().sprite(BASKET) },
  { id: 'guirlanda-pascoa', name: 'Guirlanda de Páscoa', category: 'decor', footprint: { w: 1, h: 1 }, wall: true, sprite: easterWreath().sprite(EASTER_WREATH) },
  // Carnaval
  { id: 'mascara-carnaval', name: 'Máscara de Carnaval', category: 'decor', footprint: { w: 1, h: 1 }, wall: true, sprite: carnivalMask().sprite(MASK) },
  { id: 'serpentina', name: 'Serpentina', category: 'decor', footprint: { w: 2, h: 1 }, wall: true, sprite: streamers(0).sprite(STREAMERS), frames: [streamers(1).sprite(STREAMERS)], frameMs: 600 },
  { id: 'confete', name: 'Confete', category: 'decor', footprint: { w: 1, h: 1 }, blocks: false, sprite: confetti().sprite(CONFETTI) },
  { id: 'tambor', name: 'Tambor', category: 'decor', footprint: { w: 1, h: 1 }, sprite: drum().sprite(DRUM) },
  // Aniversário do dev (agosto)
  { id: 'baloes', name: 'Balões', category: 'decor', footprint: { w: 1, h: 1 }, sprite: balloons(0).sprite(BALLOON), frames: [balloons(1).sprite(BALLOON)], frameMs: 900 },
  {
    id: 'bolo-aniversario',
    name: 'Bolo de aniversário',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: cake(0).sprite(CAKE),
    frames: [cake(1).sprite(CAKE)],
    frameMs: 400,
    glow: { color: 'rgba(255, 200, 120, 0.35)', radius: 22, dy: -10 },
  },
  { id: 'faixa-parabens', name: 'Faixa de parabéns', category: 'decor', footprint: { w: 2, h: 1 }, wall: true, sprite: banner().sprite(BANNER) },
  { id: 'presente-aniversario', name: 'Presente de aniversário', category: 'decor', footprint: { w: 1, h: 1 }, sprite: partyGift().sprite(PARTY_GIFT) },
  { id: 'chapeu-festa', name: 'Chapéu de festa', category: 'decor', footprint: { w: 1, h: 1 }, sprite: partyHat().sprite(HAT) },
  // padrão do dia a dia (discreto)
  { id: 'vaso-flores', name: 'Vaso de flores', category: 'decor', footprint: { w: 1, h: 1 }, sprite: flowerVase().sprite(VASE) },
  { id: 'quadro-agzos', name: 'Quadro Agzos', category: 'decor', footprint: { w: 1, h: 1 }, wall: true, sprite: agzosFrame().sprite(FRAME) },
  {
    id: 'luminaria-mesa',
    name: 'Luminária de mesa',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: tableLamp().sprite(LAMP),
    glow: { color: 'rgba(255, 220, 140, 0.25)', radius: 18, dy: -10 },
  },
  { id: 'planta-suspensa', name: 'Planta suspensa', category: 'decor', footprint: { w: 1, h: 1 }, wall: true, sprite: hangingPlant().sprite(HANGING) },
];

const SEASONAL_BY_ID: ReadonlyMap<string, FurnitureDef> = new Map(SEASONAL_FURNITURE.map((f) => [f.id, f]));

// ---------------------------------------------------------------- lógica

const DAY_MS = 86_400_000;

/** Dia de calendário como número inteiro (sem o efeito do horário de verão), para contar dias. */
function dayNumber(date: Date): number {
  return Math.round(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS);
}

/** Domingo de Páscoa pelo algoritmo de Meeus/Jones/Butcher (calendário gregoriano), à meia-noite local. */
export function easterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  // n / 31 dá o mês (3 = março, 4 = abril); o resto + 1 é o dia
  return new Date(year, Math.floor(n / 31) - 1, (n % 31) + 1);
}

/**
 * A estação do dia (hora local). Com prioridade de cima para baixo: carnaval, Páscoa,
 * aniversário, junina, Halloween, Natal. Sem data festiva, 'padrao'.
 */
export function seasonOf(date: Date): Season {
  const fromEaster = dayNumber(date) - dayNumber(easterDate(date.getFullYear()));
  // carnaval: de 4 dias antes da terça (Páscoa − 47) até 1 dia depois
  if (fromEaster >= -51 && fromEaster <= -46) return 'carnaval';
  // Páscoa: do Domingo de Ramos (Páscoa − 7) até a segunda depois (Páscoa + 1)
  if (fromEaster >= -7 && fromEaster <= 1) return 'pascoa';
  const month = date.getMonth(); // 0 = janeiro
  const day = date.getDate();
  if (month === 7) return 'aniversario';
  if (month === 5) return 'junina';
  if ((month === 9 && day >= 15) || (month === 10 && day <= 2)) return 'halloween';
  if (month === 11 || (month === 0 && day <= 6)) return 'natal';
  return 'padrao';
}

function pick<T>(list: readonly T[], key: string): T {
  return list[stableHash(key) % list.length]!;
}

/** Os tiles pisáveis (chaves "x,y") alcançáveis a partir das portas, sem contar os `blocked`. */
function reachable(map: RoomMap, walk: readonly (readonly boolean[])[], blocked: ReadonlySet<string>): Set<string> {
  const ok = (x: number, y: number) => walk[y]?.[x] === true && !blocked.has(`${x},${y}`);
  const queue: Array<[number, number]> = map.doors.filter((d) => ok(d.x, d.y)).map((d) => [d.x, d.y]);
  const seen = new Set(queue.map(([x, y]) => `${x},${y}`));
  for (let i = 0; i < queue.length; i++) {
    const [x, y] = queue[i]!;
    for (const [dx, dy] of DIRS4) {
      const nx = x + dx;
      const ny = y + dy;
      const k = `${nx},${ny}`;
      if (!seen.has(k) && ok(nx, ny)) {
        seen.add(k);
        queue.push([nx, ny]);
      }
    }
  }
  return seen;
}

/** Quantas fileiras acima da pegada o sprite invade (sprite mais alto que a pegada). */
function overhang(def: FurnitureDef): number {
  return Math.max(0, Math.ceil((def.sprite.h - def.footprint.h * 16) / 16));
}

/** Tiles (chaves "x,y") que um móvel ocupa no chão: a pegada e as fileiras de cima que o sprite invade. */
function floorCells(def: FurnitureDef | undefined, x: number, y: number): string[] {
  const fw = def?.footprint.w ?? 1;
  const fh = def?.footprint.h ?? 1;
  const up = def && !def.wall ? overhang(def) : 0;
  const out: string[] = [];
  for (let j = -up; j < fh; j++) for (let i = 0; i < fw; i++) out.push(`${x + i},${y + j}`);
  return out;
}

/** Decoração de uma sala: itens de parede (se a sala tem parede) e de chão, sem bloquear o caminho. */
function decorRoom(season: Season, placed: PlacedMap): SeasonalPlacement[] {
  const { map } = placed;
  if (map.kind === 'hall') return [];
  const key = `${season}:${placed.id}`;
  const { floor, wall } = SEASON_ITEMS[season];
  const density = season === 'padrao' ? PADRAO : FESTA;
  const size = mapSize(map);
  const out: SeasonalPlacement[] = [];

  // parede norte: a faixa de y=0; só salas com parede (o corredor é aberto)
  if (!map.openTop) {
    const spans: Array<[number, number]> = [];
    for (const p of map.items) {
      const def = FURNITURE_BY_ID.get(p.item);
      if (def?.wall && p.y === 0) spans.push([p.x, p.x + def.footprint.w]);
    }
    const count = 1 + (stableHash(`${key}:nw`) % density.wallMax);
    for (let k = 0; k < count; k++) {
      // o primeiro item é sorteado; o segundo é o vizinho na lista, para as duas peças não repetirem
      const first = stableHash(`${key}:w0`) % wall.length;
      const id = wall[(first + k) % wall.length]!;
      const fw = SEASONAL_BY_ID.get(id)?.footprint.w ?? 1;
      const span = size.w - fw - 1;
      if (span < 1) continue;
      const start = stableHash(`${key}:wx${k}`) % span;
      for (let i = 0; i < span; i++) {
        const x = 1 + ((start + i) % span);
        // sem vão: a parede já tem relógio e janela, e o item pode encostar neles
        if (spans.some(([a, b]) => x < b && a < x + fw)) continue;
        spans.push([x, x + fw]);
        out.push({ item: id, x: placed.x + x, y: placed.y, wall: true, roomId: placed.id });
        break;
      }
    }
  }

  // chão: tiles pisáveis, fora de qualquer móvel (até o que não bloqueia), fora das fileiras que
  // o sprite alto de um móvel invade, sem ponto de uso, sem porta e fora da linha da parede
  const data = roomData(map);
  const taken = new Set<string>();
  for (const p of map.items) for (const t of floorCells(FURNITURE_BY_ID.get(p.item), p.x, p.y)) taken.add(t);
  for (const o of out) if (o.wall) for (const t of floorCells(SEASONAL_BY_ID.get(o.item), o.x - placed.x, o.y - placed.y)) taken.add(t);
  for (const s of data.spots) taken.add(`${s.x},${s.y}`);
  for (const d of map.doors) taken.add(`${d.x},${d.y}`);

  const candidates: Array<{ x: number; y: number }> = [];
  for (let y = 1; y < size.h - 1; y++) {
    for (let x = 0; x < size.w; x++) {
      if (data.walkable[y]?.[x] && !taken.has(`${x},${y}`)) candidates.push({ x, y });
    }
  }
  candidates.sort((a, b) => stableHash(`${key}:${a.x},${a.y}`) - stableHash(`${key}:${b.x},${b.y}`));

  // cada peça só entra se não encostar em outra, se o sprite não invadir móvel e se as portas ainda alcançam tudo o que alcançavam
  const base = reachable(map, data.walkable, new Set());
  const blocked = new Set<string>();
  const want = density.floor[0] + (stableHash(`${key}:nf`) % (density.floor[1] - density.floor[0] + 1));
  for (const c of candidates) {
    if (out.filter((o) => !o.wall).length >= want) break;
    const k = `${c.x},${c.y}`;
    if (DIRS4.some(([dx, dy]) => blocked.has(`${c.x + dx},${c.y + dy}`))) continue;
    const item = pick(floor, `${key}:i:${k}`);
    if (floorCells(SEASONAL_BY_ID.get(item), c.x, c.y).some((t) => taken.has(t))) continue;
    blocked.add(k);
    // a própria peça sai da contagem, então só os outros tiles contam
    const after = reachable(map, data.walkable, blocked);
    if (after.size !== base.size - (base.has(k) ? 1 : 0)) {
      blocked.delete(k);
      continue;
    }
    out.push({ item, x: placed.x + c.x, y: placed.y + c.y, roomId: placed.id });
  }
  return out;
}

/** Decoração do exterior: gramado acima e abaixo da rua, sem encostar nos prédios nem na calçada. */
function decorExterior(layout: CampusLayout, season: Season): SeasonalPlacement[] {
  const { floor, outdoor } = SEASON_ITEMS[season];
  const items = outdoor ?? floor;
  const density = season === 'padrao' ? PADRAO : FESTA;
  const grassRow = (y: number) => (y >= 1 && y <= layout.sidewalkY - 2) || (y > layout.streetY + layout.streetH && y < layout.h - 1);
  const nearBuilding = (x: number, y: number) => layout.buildings.some((b) => x >= b.x - 1 && x <= b.x + b.w && y >= b.y - 1 && y <= b.y + b.h);
  const out: SeasonalPlacement[] = [];
  for (let y = 1; y < layout.h - 1; y++) {
    if (!grassRow(y)) continue;
    for (let x = 1; x < layout.w - 1; x++) {
      if (nearBuilding(x, y)) continue;
      if (stableHash(`${season}:ext:${x},${y}`) % density.outdoorEvery !== 0) continue;
      const item = pick(items, `${season}:exti:${x},${y}`);
      // o sprite alto sobe por cima do tile: as fileiras de cima têm que ser gramado também
      const up = overhang(SEASONAL_BY_ID.get(item)!);
      if (Array.from({ length: up }, (_, i) => y - 1 - i).some((row) => !grassRow(row))) continue;
      out.push({ item, x, y });
    }
  }
  return out;
}

/**
 * A decoração da estação para o campus inteiro. Determinística: o mesmo layout e a mesma
 * estação dão sempre a mesma lista. Coordenadas em tiles do mundo.
 */
export function seasonalDecor(layout: CampusLayout, season: Season): SeasonalPlacement[] {
  const rooms = layout.buildings.flatMap((b) => b.floors.flatMap((f) => f.rooms.flatMap((r) => decorRoom(season, r))));
  return [...rooms, ...decorExterior(layout, season)];
}
